import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";
import { queryKeys } from "../query-keys";
import {
  JobPostingSchema,
  type CreateJobPostingRequest,
  type UpdateJobPostingRequest,
} from "@applicant-reviewer/api";

// ---------------------------------------------------------------------------
// Create job posting
// ---------------------------------------------------------------------------
export function useCreateJobPosting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateJobPostingRequest) =>
      apiClient.post("/jobs", JobPostingSchema, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobPostings.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Update job posting
// ---------------------------------------------------------------------------
interface UpdateJobPostingVariables {
  id: string;
  data: UpdateJobPostingRequest;
}

export function useUpdateJobPosting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: UpdateJobPostingVariables) =>
      apiClient.put(`/jobs/${id}`, JobPostingSchema, data),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobPostings.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobPostings.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Delete job posting (soft-delete — backend sets status to "closed")
// ---------------------------------------------------------------------------
export function useDeleteJobPosting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/jobs/${id}`, JobPostingSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobPostings.lists() });
    },
  });
}
